import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import Spinner from "components/spinner";
import { Button, OutlineButton } from "components/button";
import { Status } from "components/status";
import { useSnackbarContext } from "components/snackbar";
import { formatPhoneNumber } from "utils/phoneNumberFormatter";
import { getCustomerCommunicationDetail } from "./api";
import { getLinkedRecordPath } from "./linkedRecordPath";
import {
  CommunicationChannel,
  CustomerCommunicationListItem,
} from "./types";

import { copyHtmlFormatted, htmlToCopyableText } from "./messagePreview";
import {
  buildEmailImageFilename,
  downloadEmailAsImage,
} from "./downloadEmailImage";
import {
  formatEmailTracking,
  formatSmsDeliveryStatus,
} from "./trackingDisplay";

const STATUS_COLORS: Record<string, string> = {
  sent: "0,200,100",
  failed: "239,68,68",
  queued: "234,179,8",
  pending: "160,160,160",
};

type Props = {
  customerId: string;
  channel: CommunicationChannel;
  listItem: CustomerCommunicationListItem;
  onClose: () => void;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return "--";
  return new Date(dateString).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function CommunicationDetailModal({
  customerId,
  channel,
  listItem,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const tBase = "views.customer.containers.communications";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { showSnackbar } = useSnackbarContext();
  const emailFrameRef = useRef<HTMLIFrameElement>(null);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);

  const detailQuery = useQuery(
    ["customer-communication-detail", customerId, listItem.id],
    () => getCustomerCommunicationDetail(customerId, listItem.id)
  );

  const detail = detailQuery.data;
  const messageBody = detail?.messageBody ?? "";
  const messageFormat = detail?.messageFormat ?? listItem.messageFormat;

  const handleCopy = async () => {
    const textToCopy =
      messageFormat === "html" ? htmlToCopyableText(messageBody) : messageBody;
    try {
      await navigator.clipboard.writeText(textToCopy);
      showSnackbar(tr("Copied to clipboard"));
    } catch {
      showSnackbar(tr("Failed to copy"));
    }
  };

  const handleCopyHtml = async () => {
    try {
      await copyHtmlFormatted(messageBody);
      showSnackbar(tr("Copied to clipboard"));
    } catch {
      showSnackbar(tr("Failed to copy"));
    }
  };

  const handleDownloadImage = async () => {
    const frameBody = emailFrameRef.current?.contentDocument?.body;
    if (!frameBody) {
      showSnackbar(tr("Failed to download image"));
      return;
    }

    setIsDownloadingImage(true);
    try {
      await downloadEmailAsImage(
        frameBody,
        buildEmailImageFilename(listItem.id, listItem.subject)
      );
      showSnackbar(tr("Image downloaded"));
    } catch {
      showSnackbar(tr("Failed to download image"));
    } finally {
      setIsDownloadingImage(false);
    }
  };

  const isHtmlEmail = channel === "email" && messageFormat === "html";

  const linkedPath = getLinkedRecordPath(listItem.linkedRecord);

  const recipient =
    channel === "email"
      ? listItem.destinationEmail
      : formatPhoneNumber(listItem.destinationPhone) ||
        listItem.destinationPhone;

  return (
    <div className="w-full max-w-3xl px-1">
      <div className="flex flex-col gap-4 max-h-[80vh]">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500 block">
              {channel === "email" ? tr("Email") : tr("Phone")}
            </span>
            <span className="font-medium break-all normal-case">{recipient || "--"}</span>
          </div>
          <div>
            <span className="text-gray-500 block">{tr("Date Sent")}</span>
            <span className="font-medium">{formatDate(listItem.sentAt)}</span>
          </div>
          {channel === "email" && listItem.subject && (
            <div className="sm:col-span-2">
              <span className="text-gray-500 block">{tr("Subject")}</span>
              <span className="font-medium">{listItem.subject}</span>
            </div>
          )}
          <div>
            <span className="text-gray-500 block">{tr("Status")}</span>
            <Status color={STATUS_COLORS[listItem.status] || "160,160,160"}>
              {listItem.status}
            </Status>
            {listItem.errorMessage && (
              <p className="text-red-500 text-xs mt-1">{listItem.errorMessage}</p>
            )}
          </div>
          {listItem.linkedRecord && (
            <div>
              <span className="text-gray-500 block">{tr("Linked Record")}</span>
              {linkedPath ? (
                <Link
                  to={linkedPath}
                  className="text-[#ff4f00] font-medium hover:underline"
                  onClick={onClose}
                >
                  {listItem.linkedRecord.label}
                </Link>
              ) : (
                <span className="font-medium">{listItem.linkedRecord.label}</span>
              )}
            </div>
          )}
          {listItem.sentBy && (
            <div>
              <span className="text-gray-500 block">{tr("Sent By")}</span>
              <span className="font-medium">{listItem.sentBy}</span>
            </div>
          )}
          {listItem.readReceiptApplicable && channel === "email" && (
            <div>
              <span className="text-gray-500 block">{tr("Read Receipt")}</span>
              <span className="font-medium">
                {formatEmailTracking(listItem, tr)}
              </span>
            </div>
          )}
          {channel === "sms" && (
            <div>
              <span className="text-gray-500 block">{tr("Delivery")}</span>
              <span className="font-medium">
                {formatSmsDeliveryStatus(listItem.deliveryStatus, tr)}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col min-h-0 flex-1">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <h3 className="font-semibold">{tr("Message")}</h3>
            <div className="flex items-center gap-2">
              {isHtmlEmail && (
                <OutlineButton
                  size="small"
                  onClick={handleDownloadImage}
                  disabled={!detail || isDownloadingImage}
                >
                  {isDownloadingImage ? tr("Downloading image") : tr("Download image")}
                </OutlineButton>
              )}
              {isHtmlEmail && (
                <OutlineButton
                  size="small"
                  onClick={handleCopyHtml}
                  disabled={!detail}
                >
                  {tr("Copy HTML")}
                </OutlineButton>
              )}
              <OutlineButton size="small" onClick={handleCopy} disabled={!detail}>
                {tr("Copy")}
              </OutlineButton>
            </div>
          </div>

          {detailQuery.isLoading ? (
            <Spinner />
          ) : detailQuery.isError ? (
            <p className="text-red-500 text-sm">{tr("Failed to load message")}</p>
          ) : messageFormat === "html" ? (
            <iframe
              ref={emailFrameRef}
              title={tr("Message")}
              srcDoc={messageBody}
              sandbox="allow-same-origin"
              className="w-full min-h-[240px] max-h-[40vh] border rounded bg-white select-text"
            />
          ) : (
            <pre className="w-full min-h-[120px] max-h-[40vh] overflow-auto border rounded p-3 bg-gray-50 text-sm whitespace-pre-wrap break-words select-text">
              {messageBody}
            </pre>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t">
          <Button onClick={onClose}>{tr("Close")}</Button>
        </div>
      </div>
    </div>
  );
}
